import type { ProjectToStationResponse as MeasurementResponse } from '@data-access/geospatial/alignments';
import { Vector3, Web3DViewer } from '@technology/web3d';
import { TrimbimPlugin } from '@technology/web3d-plugin-trimbim';

import { getTrbModelId } from '@trimble/web3d-providers';
import { AlignmentApiService } from '@data-access/geospatial/alignments';
import { useLogger } from '@trimble/solidjs-utils';

/**
 * Creates a function to get alignment measurements based on provided dependencies.
 * When {@link getActiveAlignmentId} returns a non-null id, that alignment is used;
 * otherwise the closest alignment model to the pick point is used.
 */
export const createGetAlignmentMeasurement = (
  alignmentApi: AlignmentApiService,
  selectedTenantTrn: () => string | null | undefined,
  getActiveAlignmentId: () => string | null | undefined
) => {
  const logger = useLogger().getLogger('createGetAlignmentMeasurement');

  return async (
    point: Vector3,
    viewer: Web3DViewer,
    useCant?: boolean,
    useGradient?: boolean
  ): Promise<MeasurementResponse | null> => {
    const tenantTrn = selectedTenantTrn();
    if (!tenantTrn) {
      throw new Error('Project is not selected!');
    }

    const getModelIdWithSmallestDistance = (
      viewer: Web3DViewer,
      point: Vector3
    ): string | null => {
      const filteredModels =
        viewer.plugins.get(TrimbimPlugin)?.getModels() || [];

      if (filteredModels.length === 0) {
        logger.warn('No models found for mission', { tenantTrn, point });
        return null;
      }

      const modelsWithDistances = filteredModels
        .map((m) => {
          return {
            modelId: getTrbModelId(m.modelId),
            distance: m.getModelBoundingBox().distanceToPoint(point),
          };
          // TrimbimPlugin.getModels() returns all trimbim models including non-alignment, so it's need to filter out non-alignment models
        })
        .filter((m) => m.modelId?.startsWith('trn:alignment:'));

      if (modelsWithDistances.length === 0) {
        logger.warn('No alignment models found', { tenantTrn, point });
        return null;
      }

      return modelsWithDistances.reduce(
        (closest, current) =>
          closest.distance <= current.distance ? closest : current,
        modelsWithDistances[0]
      ).modelId;
    };

    const activeAlignmentId = getActiveAlignmentId();
    const alignmentIdForMeasurement =
      activeAlignmentId != null && activeAlignmentId !== ''
        ? activeAlignmentId
        : getModelIdWithSmallestDistance(viewer, point);
    if (!alignmentIdForMeasurement) {
      logger.warn('No model found for point', { tenantTrn, point });
      return null;
    }

    return await alignmentApi.projectCoordinateToAlignment(
      selectedTenantTrn()!,
      alignmentIdForMeasurement,
      point,
      useCant,
      useGradient
    );
  };
};
